/**
 * GAIA conversation state: the messages (user questions and assistant
 * answers with their streamed text, the tool runs behind the action chips,
 * the transcript sent back to the model, status and error), whether a turn is
 * streaming, and the offline flags. The last 20 messages and the forced
 * offline flag persist in sessionStorage; an answer that was still streaming
 * when the page went away comes back as interrupted with a Retry.
 */
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import type { ChatMessage } from './protocol';
import type { ToolKind } from './tools';

export interface ToolRun {
  /** The model's tool-call id. */
  id: string;
  name: string;
  args: Record<string, unknown>;
  argsJson: string;
  ok: boolean;
  /** Chip label on success, the error otherwise. */
  summary: string;
  error: string | null;
  kind: ToolKind;
  /** The JSON the model received. */
  result: string;
}

export type MessageStatus = 'streaming' | 'done' | 'error' | 'stopped';

export interface GaiaMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  tools: ToolRun[];
  /** Assistant only: this turn's messages for the model (tool calls, tool results, the answer). */
  api: ChatMessage[];
  status: MessageStatus;
  error: string | null;
  /** From the stream's meta event: true when the scripted offline responder answered. */
  offline: boolean | null;
  usage: { input: number; output: number } | null;
  createdAt: number;
}

export interface GaiaState {
  messages: GaiaMessage[];
  streaming: boolean;
  /** Set by ?gaia=offline for this tab: every call asks the Worker for the offline responder. */
  forcedOffline: boolean;
  /** The last meta event's offline flag (the Worker may be offline because of GAIA_MOCK or a missing key). */
  serverOffline: boolean | null;
  /** The account the persisted conversation belongs to (sessionStorage outlives a sign-out on this tab). */
  ownerId: string | null;
  addUser: (text: string) => string;
  startAssistant: () => string;
  appendDelta: (id: string, text: string) => void;
  setMeta: (id: string, offline: boolean) => void;
  addToolRun: (id: string, run: ToolRun) => void;
  updateToolRun: (id: string, runId: string, patch: Partial<ToolRun>) => void;
  setApi: (id: string, api: ChatMessage[]) => void;
  addUsage: (id: string, usage: { input: number; output: number }) => void;
  finish: (id: string) => void;
  fail: (id: string, error: string) => void;
  stop: (id: string) => void;
  remove: (ids: string[]) => void;
  reset: () => void;
  /** Claims the conversation for an account; another account's restored messages are dropped. */
  bindOwner: (id: string) => void;
  setForcedOffline: (v: boolean) => void;
}

export const PERSIST_MESSAGES = 20;
export const STORAGE_KEY = 'gemdt-gaia';
export const INTERRUPTED = 'Interrupted: the page reloaded before this answer finished.';

let seq = 0;
function newId(prefix: string): string {
  seq += 1;
  return `${prefix}_${Date.now().toString(36)}_${seq.toString(36)}`;
}

function blank(role: GaiaMessage['role'], text: string, status: MessageStatus): GaiaMessage {
  return {
    id: newId(role === 'user' ? 'u' : 'a'),
    role,
    text,
    tools: [],
    api: [],
    status,
    error: null,
    offline: null,
    usage: null,
    createdAt: Date.now(),
  };
}

type SetState = (partial: Partial<GaiaState> | ((s: GaiaState) => Partial<GaiaState>)) => void;

function patchMessage(set: SetState, id: string, patch: (m: GaiaMessage) => Partial<GaiaMessage>): void {
  set((s) => ({ messages: s.messages.map((m) => (m.id === id ? { ...m, ...patch(m) } : m)) }));
}

function settled(messages: GaiaMessage[]): boolean {
  return !messages.some((m) => m.status === 'streaming');
}

function initializer(set: SetState): GaiaState {
  return {
    messages: [],
    streaming: false,
    forcedOffline: false,
    serverOffline: null,
    ownerId: null,
    addUser: (text) => {
      const m = blank('user', text, 'done');
      set((s) => ({ messages: [...s.messages, m] }));
      return m.id;
    },
    startAssistant: () => {
      const m = blank('assistant', '', 'streaming');
      set((s) => ({ messages: [...s.messages, m], streaming: true }));
      return m.id;
    },
    appendDelta: (id, text) => patchMessage(set, id, (m) => ({ text: m.text + text })),
    setMeta: (id, offline) => {
      patchMessage(set, id, () => ({ offline }));
      set({ serverOffline: offline });
    },
    addToolRun: (id, run) => patchMessage(set, id, (m) => ({ tools: [...m.tools, run] })),
    updateToolRun: (id, runId, patch) =>
      patchMessage(set, id, (m) => ({
        tools: m.tools.map((t) => (t.id === runId ? { ...t, ...patch } : t)),
      })),
    setApi: (id, api) => patchMessage(set, id, () => ({ api })),
    addUsage: (id, usage) =>
      patchMessage(set, id, (m) => ({
        usage: { input: (m.usage?.input ?? 0) + usage.input, output: (m.usage?.output ?? 0) + usage.output },
      })),
    finish: (id) => {
      patchMessage(set, id, () => ({ status: 'done', error: null }));
      set((s) => ({ streaming: !settled(s.messages) }));
    },
    fail: (id, error) => {
      patchMessage(set, id, () => ({ status: 'error', error }));
      set((s) => ({ streaming: !settled(s.messages) }));
    },
    stop: (id) => {
      patchMessage(set, id, (m) => (m.status === 'streaming' ? { status: 'stopped' } : {}));
      set((s) => ({ streaming: !settled(s.messages) }));
    },
    remove: (ids) =>
      set((s) => {
        const messages = s.messages.filter((m) => !ids.includes(m.id));
        return { messages, streaming: !settled(messages) };
      }),
    reset: () => set({ messages: [], streaming: false }),
    bindOwner: (id) =>
      set((s) =>
        s.ownerId !== null && s.ownerId !== id
          ? { ownerId: id, messages: [], streaming: false }
          : { ownerId: id },
      ),
    setForcedOffline: (forcedOffline) => set({ forcedOffline }),
  };
}

/** sessionStorage behind try/catch: a blocked or full storage is logged and the conversation stays in memory. */
export const safeSessionStorage: StateStorage = {
  getItem: (name) => {
    if (typeof sessionStorage === 'undefined') return null;
    try {
      return sessionStorage.getItem(name);
    } catch (err) {
      console.error('[gaia] reading the conversation from sessionStorage failed:', err);
      return null;
    }
  },
  setItem: (name, value) => {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.setItem(name, value);
    } catch (err) {
      console.error('[gaia] saving the conversation to sessionStorage failed:', err);
    }
  },
  removeItem: (name) => {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.removeItem(name);
    } catch (err) {
      console.error('[gaia] clearing the stored conversation failed:', err);
    }
  },
};

/** The last `limit` messages, starting at a question so a turn is never cut in half at the front. */
export function persistable(messages: GaiaMessage[], limit = PERSIST_MESSAGES): GaiaMessage[] {
  const tail = messages.slice(-limit);
  const first = tail.findIndex((m) => m.role === 'user');
  return first === -1 ? [] : tail.slice(first);
}

/** Restored messages: anything still streaming when the page went away is marked interrupted. */
export function restored(messages: unknown): GaiaMessage[] {
  if (!Array.isArray(messages)) return [];
  return (messages as GaiaMessage[])
    .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.text === 'string')
    .map((m) => ({
      ...m,
      tools: Array.isArray(m.tools) ? m.tools : [],
      api: Array.isArray(m.api) ? m.api : [],
      ...(m.status === 'streaming' ? { status: 'error' as const, error: INTERRUPTED } : {}),
    }));
}

export function createGaiaStore(options: { persist: boolean }): UseBoundStore<StoreApi<GaiaState>> {
  if (!options.persist) return create<GaiaState>()((set) => initializer(set));
  return create<GaiaState>()(
    persist((set) => initializer(set), {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => safeSessionStorage),
      partialize: (s) => ({
        messages: persistable(s.messages),
        forcedOffline: s.forcedOffline,
        ownerId: s.ownerId,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as { messages?: unknown; forcedOffline?: unknown; ownerId?: unknown };
        return {
          ...current,
          messages: restored(p.messages),
          forcedOffline: p.forcedOffline === true,
          ownerId: typeof p.ownerId === 'string' ? p.ownerId : null,
          streaming: false,
        };
      },
    }),
  );
}

export const useGaia = createGaiaStore({ persist: true });

/** ?gaia=offline sets the tab's offline flag, ?gaia=online clears it; any other query leaves it alone. */
export function applyOfflineFlag(
  search: string,
  store: Pick<UseBoundStore<StoreApi<GaiaState>>, 'getState'> = useGaia,
): void {
  const flag = new URLSearchParams(search).get('gaia');
  if (flag === 'offline') store.getState().setForcedOffline(true);
  else if (flag === 'online') store.getState().setForcedOffline(false);
}

// src/main.tsx applies the flag from the first address before the app renders (the column itself is a lazy
// chunk, so nothing here may depend on it having loaded), and sign-in returns with a full page load, so the
// flag is in force before any question can be sent, column open or not.
